import React from "react";
import {
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableSortLabel,
  TablePagination,
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";

// —— Netflix-ish tokens (visual only)
const COLORS = {
  bg: "#0b0b0b",
  panel: "#141414",
  text: "#ffffff",
  muted: "rgba(255,255,255,0.75)",
  border: "rgba(255,255,255,0.12)",
  red: "#E50914",
};

interface WalletHistory {
  date: string;
  amount: number;
  chip_type: "PChip" | "RChip" | "BChip";
  action: string;
}

interface WalletHistoryTableProps {
  history: WalletHistory[];
  paginatedHistory: WalletHistory[];
  filter: string;
  setFilter: (v: string) => void;
  actionFilter: string;
  setActionFilter: (v: string) => void;
  dateFilter: string;
  setDateFilter: (v: string) => void;
  orderBy: keyof WalletHistory;
  order: "asc" | "desc";
  handleSort: (property: keyof WalletHistory) => void;
  page: number;
  setPage: (v: number) => void;
  rowsPerPage: number;
  setRowsPerPage: (v: number) => void;
}

// ✅ Reusable dropdown filter component
const FilterSelect = ({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (e: any) => void;
  options: string[];
}) => (
  <FormControl size="small" sx={{ minWidth: 160 }}>
    <InputLabel
      sx={{
        fontFamily: "'Lexend', sans-serif",
        backgroundColor: COLORS.panel,
        px: 1,
        color: COLORS.muted,
      }}
    >
      {label}
    </InputLabel>
    <Select
      value={value}
      onChange={onChange}
      sx={{
        fontFamily: "'Lexend', sans-serif",
        borderRadius: "12px",
        backgroundColor: "rgba(255,255,255,0.06)",
        color: COLORS.text,
        border: `1px solid ${COLORS.border}`,
        "& .MuiOutlinedInput-notchedOutline": { borderColor: COLORS.border },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: "rgba(255,255,255,0.2)",
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: COLORS.red,
        },
        "& .MuiSvgIcon-root": {
          color: COLORS.text,
        },
      }}
    >
      {options.map((opt) => (
        <MenuItem
          key={opt}
          value={opt}
          sx={{
            backgroundColor: COLORS.panel,
            color: COLORS.text,
            "&:hover": {
              backgroundColor: "rgba(255,255,255,0.1)",
            },
          }}
        >
          {opt}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

const WalletHistoryTable: React.FC<WalletHistoryTableProps> = ({
  history,
  paginatedHistory,
  filter,
  setFilter,
  actionFilter,
  setActionFilter,
  dateFilter,
  setDateFilter,
  orderBy,
  order,
  handleSort,
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
}) => (
  <Box mt={6}>
    {/* Header + Filters */}
    <Box
      display="flex"
      flexDirection={{ xs: "column", sm: "row" }}
      alignItems={{ xs: "flex-start", sm: "center" }}
      justifyContent="space-between"
      gap={2}
      mb={3}
      flexWrap="wrap"
    >
      <Typography
        variant="h6"
        sx={{
          fontWeight: 700,
          fontFamily: "'Lexend', sans-serif",
          color: COLORS.text,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <HistoryIcon sx={{ color: COLORS.red }} /> Wallet History
      </Typography>

      <Box display="flex" gap={2} flexWrap="wrap">
        <FilterSelect
          label="Chip Type"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          options={["All", "PChip", "RChip", "BChip"]}
        />
        <FilterSelect
          label="Action"
          value={actionFilter}
          onChange={(e) => setActionFilter(e.target.value)}
          options={["All", "purchase", "promotion", "booking_earn", "redeem"]}
        />
        <FilterSelect
          label="Date"
          value={dateFilter}
          onChange={(e) => setDateFilter(e.target.value)}
          options={["All", "Last 7 Days", "This Month"]}
        />
      </Box>
    </Box>

    {/* Table */}
    <Paper
      elevation={0}
      sx={{
        borderRadius: "20px",
        overflow: "hidden",
        border: `1px solid ${COLORS.border}`,
        backgroundColor: COLORS.panel,
        boxShadow: "0 16px 48px rgba(0,0,0,0.5)",
      }}
    >
      <Box sx={{ width: "100%", overflowX: "auto" }}>
        <Table sx={{ fontFamily: "'Lexend', sans-serif", minWidth: 700 }}>
          <TableHead>
            <TableRow
              sx={{
                background: "linear-gradient(120deg,#6366f1,#8b5cf6)",
                "& th": {
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: "0.9rem",
                  padding: "16px",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                },
              }}
            >
              <TableCell>S.N</TableCell>
              <TableCell sortDirection={orderBy === "date" ? order : false}>
                <TableSortLabel
                  active={orderBy === "date"}
                  direction={order}
                  onClick={() => handleSort("date")}
                  sx={{ color: "#fff !important" }}
                >
                  Date
                </TableSortLabel>
              </TableCell>
              <TableCell>Action</TableCell>
              <TableCell>Chip Type</TableCell>
              <TableCell sortDirection={orderBy === "amount" ? order : false}>
                <TableSortLabel
                  active={orderBy === "amount"}
                  direction={order}
                  onClick={() => handleSort("amount")}
                  sx={{ color: "#fff !important" }}
                >
                  Amount
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedHistory.map((item, idx) => (
            <TableRow
              key={idx}
              hover
              sx={{
                backgroundColor:
                  idx % 2 === 0 ? COLORS.panel : "rgba(255,255,255,0.03)",
                transition: "background 0.3s ease",
                "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
              }}
            >
                <TableCell sx={{ padding: "16px", color: COLORS.muted }}>
                  {page * rowsPerPage + idx + 1}
                </TableCell>
                <TableCell
                  sx={{ padding: "16px", color: COLORS.text, fontSize: "0.9rem" }}
                >
                  {new Date(item.date).toLocaleDateString()}
                </TableCell>
                <TableCell
                  sx={{
                    textTransform: "capitalize",
                    padding: "16px",
                    color: COLORS.muted,
                  }}
                >
                  {item.action}
                </TableCell>
                <TableCell sx={{ padding: "16px" }}>
                  <Typography
                    variant="body2"
                    sx={{
                      px: 2,
                      py: 0.6,
                      borderRadius: "999px",
                      fontWeight: 600,
                      fontSize: "0.8rem",
                      color: COLORS.text,
                      backgroundColor:
                        item.chip_type === "PChip"
                          ? "rgba(248,212,66,0.2)"
                          : item.chip_type === "RChip"
                            ? "rgba(52,211,153,0.2)"
                            : "rgba(244,114,182,0.2)",
                      border: `1px solid ${
                        item.chip_type === "PChip"
                          ? "rgba(248,212,66,0.4)"
                          : item.chip_type === "RChip"
                            ? "rgba(52,211,153,0.4)"
                            : "rgba(244,114,182,0.4)"
                      }`,
                      display: "inline-block",
                      textAlign: "center",
                    }}
                  >
                    {item.chip_type}
                  </Typography>
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 700, padding: "16px", color: COLORS.text }}
                >
                  {item.amount}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={history.length}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        sx={{
          px: 3,
          fontFamily: "'Lexend', sans-serif",
          color: COLORS.text,
          "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
            {
              fontSize: "0.85rem",
              color: COLORS.muted,
            },
          "& .MuiIconButton-root": {
            color: COLORS.text,
            "&:hover": {
              backgroundColor: "rgba(255,255,255,0.1)",
            },
            "&.Mui-disabled": {
              color: COLORS.muted,
            },
          },
        }}
      />
    </Paper>
  </Box>
);

export default WalletHistoryTable;
